/**
 * DataLayer Helper
 *
 * To push events to Google Tag Manager's dataLayer for event tracking.
 * IMPORTANT: This helper requires GoogleTagManager.vue component to be mounted first
 */

// Extend Window interface to include dataLayer
declare global {
  interface Window {
    dataLayer: Array<Record<string, unknown>>;
  }
}

import { store } from "@/shared-store";
import { IVuexState } from "@/apps/merchant3/shared-models/IVuex";
import { Store } from "vuex";
import { getCustomer } from "@/apps/merchant3/services/customers.service";
import { anonymizeData } from "@/apps/merchant3/shared-helpers/anonymize.helper";

type CustomerInfo = {
  email?: string | null;
  phone?: string | null;
  firstName?: string | null;
  lastName?: string | null;
  userTag?: string | null;
  postalCode?: string | null;
};

async function addHashedUserFields(
  customer: CustomerInfo,
  eventData: Record<string, unknown>,
  userInfo: Record<string, unknown>
): Promise<void> {
  if (!customer) return;

  if (
    customer.userTag &&
    String(customer.userTag).trim() !== "" &&
    !eventData.userTag &&
    !userInfo.userTag
  ) {
    userInfo.userTag = customer.userTag;
  }

  const tasks: Array<Promise<void>> = [];

  if (
    !eventData.email &&
    customer.email &&
    String(customer.email).trim() !== ""
  ) {
    tasks.push(
      anonymizeData(String(customer.email)).then((hashed) => {
        if (hashed) userInfo.sha256_email = hashed;
      })
    );
  }

  if (
    !eventData.phone &&
    customer.phone &&
    String(customer.phone).trim() !== ""
  ) {
    const normalizedPhone = String(customer.phone).replace(/[+\s]/g, "");
    tasks.push(
      anonymizeData(normalizedPhone).then((hashed) => {
        if (hashed) userInfo.sha256_phone = hashed;
      })
    );
  }

  if (
    !eventData.first_name &&
    customer.firstName &&
    String(customer.firstName).trim() !== ""
  ) {
    tasks.push(
      anonymizeData(String(customer.firstName)).then((hashed) => {
        if (hashed) userInfo.sha256_first_name = hashed;
      })
    );
  }

  if (
    !eventData.last_name &&
    customer.lastName &&
    String(customer.lastName).trim() !== ""
  ) {
    tasks.push(
      anonymizeData(String(customer.lastName)).then((hashed) => {
        if (hashed) userInfo.sha256_last_name = hashed;
      })
    );
  }

  if (
    !eventData.zip_code &&
    customer.postalCode &&
    String(customer.postalCode).trim() !== ""
  ) {
    tasks.push(
      anonymizeData(String(customer.postalCode)).then((hashed) => {
        if (hashed) userInfo.sha256_zip_code = hashed;
      })
    );
  }

  if (tasks.length > 0) {
    await Promise.all(tasks);
  }
}

/**
 * Push an event to the dataLayer for Google Tag Manager
 *
 * @param eventName - Name of the event
 * @param eventData - Additional data to send with the event
 * @param includeUserInfo - Whether to attempt to include user information (email, phone, first_name, last_name, zip_code)
 */
export const pushToDataLayer = async (
  eventName: string,
  eventData: Record<string, unknown> = {},
  includeUserInfo = false
): Promise<void> => {
  // Ensure dataLayer exists (GTM should have initialized it)
  if (typeof window !== "undefined" && window.dataLayer) {
    const vuexStore = store as unknown as Store<IVuexState>;

    const userInfo: Record<string, unknown> = {};

    if (includeUserInfo) {
      // First try to get user information from Vuex state
      const customerState = vuexStore.state.customer as unknown as
        | CustomerInfo
        | undefined;
      const hasUserInfoInVuex =
        vuexStore.state.customer &&
        (vuexStore.state.customer.email ||
          vuexStore.state.customer.phone ||
          vuexStore.state.customer.firstName ||
          vuexStore.state.customer.lastName ||
          (customerState && customerState.postalCode));

      if (hasUserInfoInVuex) {
        const customer = vuexStore.state.customer;

        await addHashedUserFields(customer, eventData, userInfo);
      } else {
        const userTag = vuexStore.state.carts?.userTag;
        if (userTag) {
          try {
            const response = await getCustomer(userTag);
            const customer = response.data?.customer;

            if (customer) {
              await vuexStore.dispatch("customer/setCustomer", customer);

              await addHashedUserFields(customer, eventData, userInfo);
            }
          } catch (error) {
            // Silently fail - this is expected in initial pages where user doesn't exist yet
          }
        }
      }
    }

    const finalEventData = {
      event: eventName,
      ...userInfo,
      ...eventData,
    };

    window.dataLayer.push(finalEventData);

    if (process.env.NODE_ENV === "development") {
      console.debug("[DataLayer] Event pushed:", finalEventData);
    }
  } else {
    console.warn(
      "[DataLayer] dataLayer not available. Make sure GTM is initialized."
    );
  }
};
